import { z } from 'zod';
import { ERROR_MESSAGES } from '~/constants';
import { optionSchema, optionSchemaRequired } from '../../../types';
import { MALPRACTICE_CLAIM_MIN_DATE, SETTLED_CLAIM_STATUSES } from './constants';

const MIN_DATE_ERROR = 'Date must be on or after 01/01/1940';

const malpracticeClaimDateSchema = z.iso
  .date({ message: ERROR_MESSAGES.INVALID_DATE })
  .refine((date) => date >= MALPRACTICE_CLAIM_MIN_DATE, { message: MIN_DATE_ERROR });

const optionalMalpracticeClaimDateSchema = z
  .union([z.literal(''), malpracticeClaimDateSchema])
  .optional()
  .nullable();

const baseClaimSchema = z
  .object({
    id: z.number().optional(),
    providerInvolvement: optionSchemaRequired,
    claimStatus: optionSchemaRequired,
    liabilityCarrier: z
      .string()
      .min(1, ERROR_MESSAGES.REQUIRED)
      .max(256, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(256)),
    settlementAmt: z.number().optional().nullable(),
    settlementAmtFormatted: z.string().optional().nullable(),
    allegationsTxt: z
      .string()
      .min(1, ERROR_MESSAGES.REQUIRED)
      .max(4096, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096)),
    allegedInjuryTxt: z
      .string()
      .min(1, ERROR_MESSAGES.REQUIRED)
      .max(4096, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096)),
    occurrenceDate: malpracticeClaimDateSchema,
    claimDate: malpracticeClaimDateSchema,
    settlementDate: optionalMalpracticeClaimDateSchema,
    details: z.string().max(4096, ERROR_MESSAGES.MAX_CHARACTER_LIMIT(4096)).optional(),
    validFlag: z.boolean().optional(),
    warnings: z.record(z.string(), z.string()).optional(),
  })
  .refine(
    (data) => !SETTLED_CLAIM_STATUSES.includes(data.claimStatus.value) || !!data.settlementDate,
    {
      message: ERROR_MESSAGES.REQUIRED,
      path: ['settlementDate'],
    }
  );

const dateValidationSchema = z
  .object({
    occurrenceDate: z.string(),
    claimDate: z.string(),
    settlementDate: z.string().optional().nullable(),
  })
  .refine((data) => data.claimDate >= data.occurrenceDate, {
    message: 'Claim date must be after or the same as occurrence date',
    path: ['claimDate'],
  })
  .refine((data) => !data.settlementDate || data.settlementDate >= data.claimDate, {
    message: 'Settlement date must be after or the same as claim date',
    path: ['settlementDate'],
  });

const settlementValidationSchema = z
  .object({
    claimStatus: optionSchema,
    settlementDate: z.string().optional().nullable(),
    settlementAmt: z.number().optional().nullable(),
    settlementAmtFormatted: z.string().optional().nullable(),
  })
  .refine(
    (data) => !SETTLED_CLAIM_STATUSES.includes(data.claimStatus.value) || !!data.settlementDate,
    { message: 'Required', path: ['settlementDate'] }
  )
  .refine(
    (data) =>
      !SETTLED_CLAIM_STATUSES.includes(data.claimStatus.value) ||
      (!!data.settlementAmt && data.settlementAmt > 0),
    { message: ERROR_MESSAGES.REQUIRED, path: ['settlementAmtFormatted'] }
  );

export const malpracticeClaimSchema = baseClaimSchema
  .and(dateValidationSchema)
  .and(settlementValidationSchema);
